import { Reference, useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import {
  useAddQuoteDocumentMutation,
  useRequestQuoteDocumentUploadMutation,
} from "./QuoteDocuments.generated.js";

const NAME_MAX = 255;
const PDF_MIME_TYPE = "application/pdf";

export function UploadQuoteDocumentModal({
  projectId,
  quoteId,
  handleClose,
}: {
  projectId: string;
  quoteId: string;
  handleClose: () => void;
}) {
  const { t } = useTranslate(["Quote", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const [addQuoteDocument] = useAddQuoteDocumentMutation({ client });
  const [requestUpload] = useRequestQuoteDocumentUploadMutation({ client });

  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  // Null until the user types: the name follows the picked file's name for as
  // long as they leave it alone.
  const [editedName, setEditedName] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const name = editedName ?? file?.name ?? "";
  const trimmed = name.trim();
  const isNameValid = trimmed.length > 0 && trimmed.length <= NAME_MAX;
  const canSubmit = Boolean(file) && !fileError && isNameValid && !submitting;

  return (
    <Modal
      title={t("Upload PDF", { ns: "Quote" })}
      handleClose={handleClose}
      maxWidth="xs"
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text", disabled: submitting },
        },
        {
          label: t("Upload", { ns: "Global" }),
          handleClick: async () => {
            if (!file || !canSubmit) return;
            setSubmitting(true);
            try {
              // Straight into the fileshare bucket, at a key the backend minted
              // and a URL that authorises writing only that one object. The
              // bytes never pass through the backend, and it is never handed a
              // URL to go and fetch.
              const requested = await requestUpload({
                variables: { input: { projectId, quoteId } },
              });
              const upload = requested.data?.requestQuoteDocumentUpload;
              if (!upload) throw new Error("could not start the upload");

              const response = await fetch(upload.uploadUrl, {
                method: "PUT",
                body: file,
                headers: { "Content-Type": PDF_MIME_TYPE },
              });
              if (!response.ok) {
                throw new Error(
                  t("Upload failed. Please try again.", { ns: "Quote" })
                );
              }

              await addQuoteDocument({
                variables: {
                  input: {
                    projectId,
                    quoteId,
                    name: trimmed,
                    uploadKey: upload.uploadKey,
                  },
                },
                // Written into the cache from the mutation result rather than
                // refetched, because the list read goes to a replica (D13).
                update: (cache, { data }) => {
                  const quoteDocument = data?.addQuoteDocument.quoteDocument;
                  if (!quoteDocument) return;
                  cache.modify<{ documents: Reference[] }>({
                    id: cache.identify({ __typename: "Quote", id: quoteId }),
                    fields: {
                      documents(existing = [], { toReference }) {
                        const newRef = toReference(quoteDocument);
                        return newRef ? [newRef, ...existing] : existing;
                      },
                    },
                  });
                },
              });
              handleClose();
            } catch (error) {
              if (error instanceof Error)
                enqueueSnackbar(error.message, { variant: "error" });
            } finally {
              setSubmitting(false);
            }
          },
          buttonProps: { variant: "contained", disabled: !canSubmit },
        },
      ]}
    >
      <Stack direction="column" spacing={2}>
        <Alert severity="info">
          {t(
            "Saved documents are visible to every organisation with read access to this quote.",
            { ns: "Quote" }
          )}
        </Alert>

        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadFileIcon />}
            disabled={submitting}
            onClick={() => inputRef.current?.click()}
          >
            {t("Select file", { ns: "Quote" })}
          </Button>
          <Typography
            variant="body2"
            color={fileError ? "error" : "text.secondary"}
            noWrap
          >
            {fileError ?? file?.name}
          </Typography>
        </Stack>

        <TextField
          variant="outlined"
          label={t("Document name", { ns: "Quote" })}
          value={name}
          onChange={event => setEditedName(event.target.value)}
          slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
          error={name.length > 0 && !isNameValid}
          disabled={!file || submitting}
          fullWidth
          size="small"
        />

        <input
          ref={inputRef}
          type="file"
          accept={PDF_MIME_TYPE}
          hidden
          // `accept` is only a hint to the picker, so the type is checked here
          // too rather than being discovered by the backend after an upload.
          // Only a type that contradicts PDF is refused: the browser leaves
          // `type` empty when it cannot tell, and a real PDF picked on such a
          // system would otherwise never reach the backend's own byte check.
          onChange={event => {
            const selected = event.target.files?.[0] ?? null;
            setFile(selected);
            setEditedName(null);
            setFileError(
              selected &&
                selected.type !== "" &&
                selected.type !== PDF_MIME_TYPE
                ? t("Only PDF files can be added as quote documents.", {
                    ns: "Quote",
                  })
                : null
            );
          }}
        />
      </Stack>
    </Modal>
  );
}
